// components/dashboard/credits/CreditsBuyCreditsModal.tsx

'use client'

import {
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  Check,
  ChevronLeft,
  Loader2,
  Sparkles,
  Zap,
} from 'lucide-react'

import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'

import {
  Skeleton,
} from '@/components/ui/skeleton'

import {
  cn,
} from '@/lib/utils'

import useSWR from 'swr'

import {
  useSellerId,
} from '@/hooks/useSellerId'

import {
  CheckoutFormBlock,
} from '@/components/funnel/blocks/CheckoutFormBlock'

import {
  buildPurchaseContext,
} from '@/lib/checkout/buildPurchaseContext'

type CreditPackage = {
  id:
    string

  name:
    string

  credits:
    number

  price:
    number

  priceLabel?:
    string

  popular?:
    boolean

  description?:
    string

  features?:
    string[]

  packKey?:
    string

  product_id?:
    number
}

type PackagesRes = {
  ok:
    boolean

  packages:
    CreditPackage[]
}

type ProductLike =
  any

type Step =
  | 'pick'
  | 'checkout'

const FULL_ENERGY_CREDITS =
  10000

function creditsToEnergyPercent(
  credits:
    number
) {
  if (
    !Number.isFinite(
      credits
    ) ||
    credits <= 0
  ) {
    return 0
  }

  return (
    credits /
    FULL_ENERGY_CREDITS
  ) * 100
}

function formatPercent(
  value:
    number
) {
  const rounded =
    Math.round(
      value * 10
    ) / 10

  return Number.isInteger(
    rounded
  )
    ? String(
        rounded
      )
    : rounded.toFixed(
        1
      )
}

function formatMoney(
  value:
    number,

  currency =
    'USD'
) {
  return new Intl
    .NumberFormat(
      'en-US',
      {
        style:
          'currency',

        currency,

        minimumFractionDigits:
          2,

        maximumFractionDigits:
          2,
      }
    )
    .format(
      Number(
        value ||
        0
      )
    )
}

function getPackageLabel(
  index:
    number,

  total:
    number
) {
  if (
    total === 1
  ) {
    return 'Carga de energía'
  }

  if (
    index === 0
  ) {
    return 'Carga ligera'
  }

  if (
    index ===
    total - 1
  ) {
    return 'Carga total'
  }

  return 'Carga pro'
}

export function CreditsBuyCreditsModal({
  open,
  onOpenChange,
  onCreditsAdded,
  initialPackKey,
}: {
  open:
    boolean

  onOpenChange:
    (
      value:
        boolean
    ) => void

  onCreditsAdded?:
    () => void

  initialPackKey?:
    string | null
}) {
  const [
    step,
    setStep,
  ] =
    useState<Step>(
      'pick'
    )

  const {
    sellerId,
  } =
    useSellerId()

  const [
    loading,
    setLoading,
  ] =
    useState(false)

  const [
    err,
    setErr,
  ] =
    useState<
      string | null
    >(null)

  const [
    packages,
    setPackages,
  ] =
    useState<
      CreditPackage[]
    >([])

  const [
    selected,
    setSelected,
  ] =
    useState<
      CreditPackage | null
    >(null)

  const [
    selectedIndex,
    setSelectedIndex,
  ] =
    useState(0)

  const fetcher =
    async (
      url:
        string
    ) => {
      const response =
        await fetch(
          url,
          {
            credentials:
              'include',
          }
        )

      const data =
        await response
          .json()

      if (
        !response.ok
      ) {
        throw new Error(
          data?.error ||
          'ENERGY_LOAD_FAILED'
        )
      }

      return data
    }

  /*
   * Checkout.
   */

  const [
    loadingProduct,
    setLoadingProduct,
  ] =
    useState(false)

  const [
    productErr,
    setProductErr,
  ] =
    useState<
      string | null
    >(null)

  const [
    selectedProduct,
    setSelectedProduct,
  ] =
    useState<
      ProductLike | null
    >(null)

  const [
    selectedPackKey,
    setSelectedPackKey,
  ] =
    useState('')

  /*
   * Ordenamos siempre por tamaño
   * de carga.
   */

  const {
    data:
      progressResponse,

    mutate:
      refreshEnergy,
  } =
    useSWR(
      open &&
      sellerId
        ? `/api/moments/${sellerId}/progress`
        : null,

      fetcher,

      {
        revalidateOnFocus:
          false,

        revalidateOnReconnect:
          false,
      }
    )

  const progress =
    progressResponse
      ?.progress ??
    progressResponse
      ?.data ??
    progressResponse

  const energy =
    progress
      ?.energy

  const currentEnergyPercent =
    Math.max(
      0,
      Number(
        energy?.percent ||
        0
      )
    )

  const currentReservePercent =
    Math.max(
      0,
      Number(
        energy
          ?.reservePercent ||
        0
      )
    )

  const sortedPackages =
    useMemo(
      () =>
        [...packages].sort(
          (
            a,
            b
          ) =>
            Number(
              a.credits ||
              0
            ) -
            Number(
              b.credits ||
              0
            )
        ),
      [
        packages,
      ]
    )

  /*
   * Al cargar paquetes seleccionamos:
   *
   * 1. popular
   * 2. paquete intermedio
   * 3. primero
   */

  useEffect(() => {
    if (
      !sortedPackages.length
    ) {
      return
    }

    const popularIndex =
      sortedPackages.findIndex(
        item =>
          Boolean(
            item.popular
          )
      )

    const defaultIndex =
      popularIndex >= 0
        ? popularIndex
        : Math.floor(
            (
              sortedPackages.length -
              1
            ) /
            2
          )

    setSelectedIndex(
      defaultIndex
    )

    setSelected(
      sortedPackages[
        defaultIndex
      ] ||
      null
    )
  }, [
    sortedPackages,
  ])

  /*
   * Initial pack mantiene
   * compatibilidad con el flujo
   * anterior.
   */

  useEffect(() => {
    if (
      !open ||
      !initialPackKey ||
      !sortedPackages.length ||
      step !==
        'pick'
    ) {
      return
    }

    const index =
      sortedPackages.findIndex(
        item =>
          String(
            item.packKey ||
            item.id ||
            ''
          ) ===
          String(
            initialPackKey
          )
      )

    if (
      index < 0
    ) {
      return
    }

    setSelectedIndex(
      index
    )

    setSelected(
      sortedPackages[
        index
      ]
    )
  }, [
    open,
    initialPackKey,
    sortedPackages,
    step,
  ])

  /*
   * Reset al cerrar.
   */

  useEffect(() => {
    if (
      open
    ) {
      return
    }

    setStep(
      'pick'
    )

    setSelected(
      null
    )

    setSelectedProduct(
      null
    )

    setSelectedPackKey(
      ''
    )

    setErr(
      null
    )

    setProductErr(
      null
    )

    setLoading(
      false
    )

    setLoadingProduct(
      false
    )
  }, [
    open,
  ])

  /*
   * Load packages.
   */

  useEffect(() => {
    if (
      !open
    ) {
      return
    }

    let alive =
      true

    async function loadPackages() {
      setLoading(
        true
      )

      setErr(
        null
      )

      try {
        const response =
          await fetch(
            `/api/credits/packages?sellerId=${encodeURIComponent(
              String(sellerId ?? 0)
            )}`,
            {
              method:
                'GET',

              cache:
                'no-store',
            }
          )

        if (
          !response.ok
        ) {
          throw new Error(
            `HTTP ${response.status}`
          )
        }

        const data =
          await response
            .json() as PackagesRes

        if (
          !data?.ok
        ) {
          throw new Error(
            'LOAD_FAILED'
          )
        }

        if (
          alive
        ) {
          setPackages(
            Array.isArray(
              data.packages
            )
              ? data.packages
              : []
          )
        }
      } catch (
        error:
          any
      ) {
        if (
          alive
        ) {
          setErr(
            error?.message ||
            'LOAD_FAILED'
          )
        }
      } finally {
        if (
          alive
        ) {
          setLoading(
            false
          )
        }
      }
    }

    void loadPackages()

    return () => {
      alive =
        false
    }
  }, [
    open,
    sellerId,
  ])

  const selectedEnergyPercent =
    useMemo(
      () =>
        creditsToEnergyPercent(
          Number(
            selected
              ?.credits ||
            0
          )
        ),
      [
        selected,
      ]
    )

  /*
   * Energía total antes de comprar:
   *
   * energía activa + reserva.
   */

  const currentTotalEnergy =
    Math.max(
      0,
      Number(
        currentEnergyPercent ||
        0
      )
    ) +
    Math.max(
      0,
      Number(
        currentReservePercent ||
        0
      )
    )

  const resultingTotalEnergy =
    currentTotalEnergy +
    selectedEnergyPercent

  const resultingActiveEnergy =
    Math.min(
      100,
      resultingTotalEnergy
    )

  const resultingReserveEnergy =
    Math.max(
      0,
      resultingTotalEnergy -
      100
    )

  const progressForRing =
    Math.min(
      100,
      selectedEnergyPercent
    )

  const sliderMax =
    Math.max(
      0,
      sortedPackages.length -
      1
    )

  const sliderProgress =
    sliderMax > 0
      ? (
          selectedIndex /
          sliderMax
        ) *
        100
      : 100

  const recommendedIndex =
    useMemo(
      () => {
        const popularIndex =
          sortedPackages
            .findIndex(
              item =>
                Boolean(
                  item.popular
                )
            )

        if (
          popularIndex >= 0
        ) {
          return popularIndex
        }

        return Math.floor(
          (
            sortedPackages.length -
            1
          ) /
          2
        )
      },
      [
        sortedPackages,
      ]
    )

  const modalClass =
    useMemo(
      () =>
        cn(
          `
            overflow-hidden
            border
            border-border
            bg-background/95
            p-0
            text-foreground
            shadow-[0_30px_120px_rgba(0,0,0,.28)]
            backdrop-blur-3xl

            [&>button]:hidden
          `,

          step ===
            'pick'
            ? `
              max-h-[92vh]
              max-w-[560px]
              rounded-[34px]
            `
            : `
              max-h-[92vh]
              max-w-[460px]
              rounded-[30px]
            `
        ),
      [
        step,
      ]
    )

  function choosePackage(
    index:
      number
  ) {
    const pack =
      sortedPackages[
        index
      ]

    if (
      !pack
    ) {
      return
    }

    setSelectedIndex(
      index
    )

    setSelected(
      pack
    )
  }

  async function loadPackProduct(
    pack:
      CreditPackage
  ) {
    const packKey =
      String(
        pack.packKey ||
        pack.id ||
        ''
      )

    setSelectedPackKey(
      packKey
    )

    setLoadingProduct(
      true
    )

    setProductErr(
      null
    )

    setSelectedProduct(
      null
    )

    try {
      const response =
        await fetch(
          `/api/credits/product?pack=${encodeURIComponent(
            packKey
          )}&sellerId=${encodeURIComponent(
            String(sellerId ?? 0)
          )}`,
          {
            cache:
              'no-store',
          }
        )

      const data =
        await response
          .json()
          .catch(
            () => null
          )

      if (
        !response.ok
      ) {
        setProductErr(
          data?.error ||
          `HTTP ${response.status}`
        )

        return
      }

      setSelectedProduct(
        data
      )
    } catch (
      error
    ) {
      console.error(
        '[ENERGY PRODUCT LOAD ERROR]',
        error
      )

      setProductErr(
        'Error al preparar la recarga.'
      )
    } finally {
      setLoadingProduct(
        false
      )
    }
  }

  async function continueRecharge() {
    if (
      !selected
    ) {
      return
    }

    setStep(
      'checkout'
    )

    await loadPackProduct(
      selected
    )
  }

  function backToPick() {
    setStep(
      'pick'
    )

    setSelectedProduct(
      null
    )

    setSelectedPackKey(
      ''
    )

    setProductErr(
      null
    )

    setLoadingProduct(
      false
    )
  }

  return (
    <Dialog
      open={
        open
      }

      onOpenChange={
        onOpenChange
      }
    >
      <DialogContent
        className={
          modalClass
        }
      >
        {step ===
        'pick' ? (
          /*
           * ========================================
           * ENERGY PICKER
           * ========================================
           */

          <div
            className="
              relative
              max-h-[92vh]
              overflow-y-auto
              overscroll-contain
              px-5
              pb-6
              pt-6
              sm:px-7
              sm:pb-8
              sm:pt-7
              [scrollbar-width:none]
              [&::-webkit-scrollbar]:hidden
            "
          >
            {/* AMBIENT GLOW */}

            <div
              aria-hidden

              className="
                pointer-events-none
                absolute
                left-1/2
                top-[-90px]
                h-[280px]
                w-[280px]
                -translate-x-1/2
                rounded-full
                bg-primary/[0.11]
                blur-[90px]
              "
            />

            {/* ICON */}

            <div
              className="
                relative
                mx-auto
                flex
                h-[62px]
                w-[62px]
                items-center
                justify-center
                rounded-full
                border
                border-primary/35
                bg-primary/[0.07]
                text-primary
                shadow-[0_0_35px_hsl(var(--primary)/.14)]
              "
            >
              <Zap
                className="
                  h-7
                  w-7
                "
              />
            </div>

            <div
              className="
                relative
                mt-5
                text-center
              "
            >
              <h2
                className="
                  text-[28px]
                  font-semibold
                  tracking-[-.045em]
                  text-foreground
                  sm:text-[31px]
                "
              >
                Recargar energía
              </h2>

              <p
                className="
                  mx-auto
                  mt-2
                  max-w-[390px]
                  text-[13px]
                  leading-relaxed
                  text-muted-foreground
                  sm:text-[14px]
                "
              >
                Dale más autonomía a tu AIA
                para crear contenido,
                analizar oportunidades y
                ejecutar tareas.
              </p>
            </div>

            {loading ? (
              <div
                className="
                  mt-8
                  space-y-4
                "
              >
                <Skeleton
                  className="
                    mx-auto
                    h-[230px]
                    w-[230px]
                    rounded-full
                    bg-muted/50
                  "
                />

                <div
                  className="
                    grid
                    grid-cols-3
                    gap-2
                  "
                >
                  {Array.from({
                    length:
                      3,
                  }).map(
                    (
                      _,
                      index
                    ) => (
                      <Skeleton
                        key={
                          index
                        }
                        className="
                          h-[118px]
                          rounded-[22px]
                          bg-muted/50
                        "
                      />
                    )
                  )}
                </div>
              </div>
            ) : err ? (
              <div
                className="
                  mt-8
                  rounded-[22px]
                  border
                  border-border/80
                  bg-white/[0.03]
                  p-5
                  text-center
                "
              >
                <p
                  className="
                    text-[12px]
                    text-muted-foreground
                  "
                >
                  No pude cargar las
                  opciones de energía.
                </p>

                <button
                  type="button"

                  onClick={() => {
                    onOpenChange(
                      false
                    )

                    window.setTimeout(
                      () => {
                        onOpenChange(
                          true
                        )
                      },
                      60
                    )
                  }}

                  className="
                    mt-4
                    rounded-full
                    border
                    border-primary/25
                    bg-primary/[0.08]
                    px-4
                    py-2
                    text-[11px]
                    font-medium
                    text-primary
                  "
                >
                  Reintentar
                </button>
              </div>
            ) : !selected ? (
              <div
                className="
                  mt-8
                  rounded-[22px]
                  border
                  border-border/80
                  bg-white/[0.03]
                  p-6
                  text-center
                  text-[12px]
                  text-muted-foreground
                "
              >
                No hay cargas de energía
                disponibles.
              </div>
            ) : (
              <>
                {/* =================================
                    ENERGY DIAL
                ================================= */}

                <div
                  className="
                    relative
                    mx-auto
                    mt-7
                    flex
                    h-[232px]
                    w-[232px]
                    items-center
                    justify-center
                  "
                >
                  <div
                    aria-hidden

                    className="
                      absolute
                      inset-[28px]
                      rounded-full
                      bg-primary/[0.07]
                      blur-[38px]
                    "
                  />

                  <svg
                    viewBox="0 0 220 220"

                    className="
                      absolute
                      inset-0
                      h-full
                      w-full
                      -rotate-90
                    "
                  >
                    <circle
                      cx="110"
                      cy="110"
                      r="94"

                      fill="none"

                      stroke="rgba(255,255,255,.08)"

                      strokeWidth="9"
                    />

                    <circle
                      cx="110"
                      cy="110"
                      r="94"

                      fill="none"

                      stroke="hsl(var(--primary))"

                      strokeWidth="9"

                      strokeLinecap="round"

                      pathLength="100"

                      strokeDasharray={`
                        ${progressForRing}
                        100
                      `}

                      className="
                        drop-shadow-[0_0_10px_hsl(var(--primary)/.35)]
                        transition-all
                        duration-500
                      "
                    />
                  </svg>

                  <div
                    className="
                      relative
                      flex
                      flex-col
                      items-center
                      text-center
                    "
                  >
                    <Zap
                      className="
                        mb-2
                        h-5
                        w-5
                        text-primary
                      "
                    />

                    <span
                      className="
                        text-[48px]
                        font-light
                        leading-none
                        tracking-[-.065em]
                        text-foreground
                      "
                    >
                      +
                      {formatPercent(
                        selectedEnergyPercent
                      )}
                      %
                    </span>

                    <span
                      className="
                        mt-2
                        text-[9px]
                        font-medium
                        uppercase
                        tracking-[.22em]
                        text-muted-foreground/70
                      "
                    >
                      de energía
                    </span>

                    <div
                      className="
                        mt-4
                        rounded-full
                        border
                        border-border/90
                        bg-background/60
                        px-4
                        py-2
                      "
                    >
                      <p
                        className="
                          text-[14px]
                          font-semibold
                          text-primary
                        "
                      >
                        {
                          selected.priceLabel ||
                          formatMoney(
                            selected.price
                          )
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* =================================
                    DISCRETE SLIDER
                ================================= */}

                {sortedPackages.length >
                  1 && (
                  <div
                    className="
                      mx-auto
                      mt-1
                      max-w-[410px]
                      px-2
                    "
                  >
                    <input
                      type="range"

                      min={
                        0
                      }

                      max={
                        sliderMax
                      }

                      step={
                        1
                      }

                      value={
                        selectedIndex
                      }

                      onChange={
                        event => {
                          choosePackage(
                            Number(
                              event.target
                                .value
                            )
                          )
                        }
                      }

                      aria-label="Seleccionar carga de energía"

                      className="
                        h-2
                        w-full
                        cursor-pointer
                        accent-primary
                      "

                      style={{
                        background: `
                          linear-gradient(
                            to right,
                            hsl(var(--primary))
                              0%,
                            hsl(var(--primary))
                              ${sliderProgress}%,
                            rgba(255,255,255,.08)
                              ${sliderProgress}%,
                            rgba(255,255,255,.08)
                              100%
                          )
                        `,
                      }}
                    />

                    <div
                      className="
                        mt-1
                        flex
                        justify-between
                        text-[9px]
                        text-foreground/25
                      "
                    >
                      <span>
                        {
                          formatPercent(
                            creditsToEnergyPercent(
                              sortedPackages[
                                0
                              ]
                                ?.credits ||
                              0
                            )
                          )
                        }
                        %
                      </span>

                      <span>
                        {
                          formatPercent(
                            creditsToEnergyPercent(
                              sortedPackages[
                                sortedPackages.length -
                                1
                              ]
                                ?.credits ||
                              0
                            )
                          )
                        }
                        %
                      </span>
                    </div>
                  </div>
                )}

                {/* =================================
                    PACKAGES
                ================================= */}

                <div
                  className={cn(
                    `
                      mt-5
                      grid
                      gap-2
                    `,

                    sortedPackages.length >=
                      3
                      ? 'grid-cols-3'
                      : sortedPackages.length ===
                          2
                        ? 'grid-cols-2'
                        : 'grid-cols-1'
                  )}
                >
                  {sortedPackages.map(
                    (
                      pack,
                      index
                    ) => {
                      const active =
                        index ===
                        selectedIndex

                      const energyAmount =
                        creditsToEnergyPercent(
                          Number(
                            pack.credits ||
                            0
                          )
                        )

                      const recommended =
                        index ===
                        recommendedIndex

                      return (
                        <button
                          key={
                            pack.id
                          }

                          type="button"

                          onClick={() => {
                            choosePackage(
                              index
                            )
                          }}

                          className={cn(
                            `
                              relative
                              min-w-0
                              rounded-[22px]
                              border
                              px-2
                              pb-4
                              pt-5
                              text-center
                              transition
                              duration-200
                              active:scale-[.98]
                            `,

                            active
                              ? `
                                  border-primary/80
                                  bg-primary/10
                                  shadow-[0_0_28px_hsl(var(--primary)/.10)]
                                `
                              : `
                                  border-border
                                  bg-muted/30
                                `
                          )}
                        >
                          {recommended && (
                            <span
                              className="
                                absolute
                                left-1/2
                                top-[-8px]
                                -translate-x-1/2
                                whitespace-nowrap
                                rounded-full
                                bg-primary
                                px-2
                                py-1
                                text-[7px]
                                font-bold
                                uppercase
                                tracking-[.08em]
                                text-primary-foreground
                              "
                            >
                              Recomendado
                            </span>
                          )}

                          <Zap
                            className={cn(
                              `
                                mx-auto
                                h-4
                                w-4
                              `,

                              active
                                ? 'text-primary'
                                : 'text-muted-foreground/70'
                            )}
                          />

                          <p
                            className="
                              mt-2
                              truncate
                              text-[10px]
                              font-medium
                              text-foreground/70
                              sm:text-[11px]
                            "
                          >
                            {getPackageLabel(
                              index,
                              sortedPackages.length
                            )}
                          </p>

                          <p
                            className="
                              mt-1
                              text-[23px]
                              font-light
                              tracking-[-.045em]
                              text-foreground
                              sm:text-[27px]
                            "
                          >
                            +
                            {formatPercent(
                              energyAmount
                            )}
                            %
                          </p>

                          <p
                            className="
                              mt-1
                              truncate
                              text-[11px]
                              text-muted-foreground/80
                              sm:text-[10px]
                            "
                          >
                            {
                              pack.priceLabel ||
                              formatMoney(
                                pack.price
                              )
                            }
                          </p>
                        </button>
                      )
                    }
                  )}
                </div>

                {/* =================================
                    RESULTING ENERGY
                ================================= */}

                <div
                  className="
                    mt-5
                    rounded-[20px]
                    border
                    border-border
                    bg-muted/30
                    px-4
                    py-3.5
                  "
                >
                  <div
                    className="
                      flex
                      items-center
                      gap-3
                    "
                  >
                    <div
                      className="
                        flex
                        h-9
                        w-9
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-primary/[0.10]
                        text-primary
                      "
                    >
                      <Sparkles
                        className="
                          h-4
                          w-4
                        "
                      />
                    </div>

                    <div
                      className="
                        min-w-0
                        flex-1
                      "
                    >
                      <p
                        className="
                          text-[12px]
                          text-muted-foreground/80
                        "
                      >
                        Después de recargar
                      </p>

                      {resultingReserveEnergy >
                      0 ? (
                        <p
                          className="
                            mt-0.5
                            text-[14px]
                            font-medium
                            text-foreground/80
                          "
                        >
                          Tu AIA quedará con{' '}
                          <span
                            className="
                              text-primary
                            "
                          >
                            {formatPercent(
                              resultingActiveEnergy
                            )}
                            %
                          </span>{' '}
                          +{' '}
                          <span
                            className="
                              text-primary
                            "
                          >
                            {formatPercent(
                              resultingReserveEnergy
                            )}
                            %
                          </span>{' '}
                          de reserva
                        </p>
                      ) : (
                        <p
                          className="
                            mt-0.5
                            text-[12px]
                            font-medium
                            text-foreground/80
                          "
                        >
                          Tu AIA quedará con{' '}
                          <span
                            className="
                              text-primary
                            "
                          >
                            {formatPercent(
                              resultingActiveEnergy
                            )}
                            %
                          </span>{' '}
                          de energía
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* CTA */}

                <button
                  type="button"

                  onClick={
                    continueRecharge
                  }

                  className="
                    mt-5
                    flex
                    h-[54px]
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded-[18px]
                    bg-primary
                    px-5
                    text-[13px]
                    font-semibold
                    text-primary-foreground
                    shadow-[0_0_30px_hsl(var(--primary)/.14)]
                    transition
                    active:scale-[.985]
                  "
                >
                  <Zap
                    className="
                      h-4
                      w-4
                    "
                  />

                  Continuar recarga
                </button>

                <button
                  type="button"

                  onClick={() => {
                    onOpenChange(
                      false
                    )
                  }}

                  className="
                    mt-3
                    w-full
                    py-2
                    text-[11px]
                    text-muted-foreground/70
                    transition
                    hover:text-foreground/55
                  "
                >
                  Cancelar
                </button>
              </>
            )}
          </div>
        ) : (
          /*
           * ========================================
           * CHECKOUT
           * ========================================
           */

          <div
            className="
              max-h-[92vh]
              overflow-y-auto
              overscroll-contain
              px-5
              pb-6
              pt-5
              sm:px-6
              [scrollbar-width:none]
              [&::-webkit-scrollbar]:hidden
            "
          >
            <div
              className="
                flex
                items-center
                gap-3
              "
            >
              <button
                type="button"

                onClick={
                  backToPick
                }

                className="
                  flex
                  h-9
                  w-9
                  shrink-0
                  items-center
                  justify-center
                  rounded-full
                  border
                  border-border/80
                  bg-muted/40
                  text-foreground/55
                  transition
                  active:scale-95
                "
              >
                <ChevronLeft
                  className="
                    h-4
                    w-4
                  "
                />
              </button>

              <div>
                <h2
                  className="
                    text-[20px]
                    font-semibold
                    tracking-[-.03em]
                    text-foreground
                  "
                >
                  Completar recarga
                </h2>

                <p
                  className="
                    mt-0.5
                    text-[13px]
                    text-muted-foreground/70
                  "
                >
                  Tu energía se agregará
                  automáticamente
                </p>
              </div>
            </div>

            {selected && (
              <div
                className="
                  mt-5
                  rounded-[22px]
                  border
                  border-primary/20
                  bg-primary/[0.055]
                  px-4
                  py-4
                "
              >
                <div
                  className="
                    flex
                    items-center
                    justify-between
                    gap-4
                  "
                >
                  <div
                    className="
                      flex
                      items-center
                      gap-3
                    "
                  >
                    <div
                      className="
                        flex
                        h-10
                        w-10
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-primary/[0.12]
                        text-primary
                      "
                    >
                      <Zap
                        className="
                          h-4
                          w-4
                        "
                      />
                    </div>

                    <div>
                      <p
                        className="
                          text-[11px]
                          font-medium
                          text-foreground/75
                        "
                      >
                        +
                        {formatPercent(
                          selectedEnergyPercent
                        )}
                        % de energía
                      </p>

                      {resultingReserveEnergy >
                      0 ? (
                        <p
                          className="
                            mt-1
                            text-[13px]
                            text-muted-foreground/80
                          "
                        >
                          100% activa +{' '}
                          {formatPercent(
                            resultingReserveEnergy
                          )}
                          % reserva
                        </p>
                      ) : (
                        <p
                          className="
                            mt-1
                            text-[11px]
                            text-muted-foreground/80
                          "
                        >
                          Quedará en{' '}
                          {formatPercent(
                            resultingActiveEnergy
                          )}
                          %
                        </p>
                      )}
                    </div>
                  </div>

                  <p
                    className="
                      shrink-0
                      text-[16px]
                      font-semibold
                      text-primary
                    "
                  >
                    {
                      selected.priceLabel ||
                      formatMoney(
                        selected.price
                      )
                    }
                  </p>
                </div>
              </div>
            )}

            {loadingProduct && (
              <div
                className="
                  mt-4
                  space-y-3
                  rounded-[22px]
                  border
                  border-border/80
                  bg-muted/30
                  p-4
                "
              >
                <Skeleton
                  className="
                    h-4
                    w-40
                    bg-white/[0.06]
                  "
                />

                <Skeleton
                  className="
                    h-11
                    w-full
                    rounded-2xl
                    bg-white/[0.06]
                  "
                />

                <Skeleton
                  className="
                    h-11
                    w-full
                    rounded-2xl
                    bg-white/[0.06]
                  "
                />

                <Skeleton
                  className="
                    h-11
                    w-full
                    rounded-2xl
                    bg-white/[0.06]
                  "
                />
              </div>
            )}

            {!loadingProduct &&
              productErr && (
              <div
                className="
                  mt-4
                  rounded-[22px]
                  border
                  border-border/80
                  bg-muted/30
                  p-4
                "
              >
                <p
                  className="
                    text-[11px]
                    text-muted-foreground
                  "
                >
                  No pude preparar la
                  recarga: {productErr}
                </p>

                <div
                  className="
                    mt-3
                    flex
                    gap-2
                  "
                >
                  <button
                    type="button"

                    onClick={() => {
                      if (
                        selected
                      ) {
                        void loadPackProduct(
                          selected
                        )
                      }
                    }}

                    className="
                      flex
                      h-10
                      flex-1
                      items-center
                      justify-center
                      rounded-[14px]
                      bg-primary
                      text-[10px]
                      font-semibold
                      text-primary-foreground
                    "
                  >
                    Reintentar
                  </button>

                  <button
                    type="button"

                    onClick={
                      backToPick
                    }

                    className="
                      flex
                      h-10
                      flex-1
                      items-center
                      justify-center
                      rounded-[14px]
                      border
                      border-white/10
                      bg-muted/40
                      text-[10px]
                      font-medium
                      text-foreground/60
                    "
                  >
                    Cambiar carga
                  </button>
                </div>
              </div>
            )}

            {!loadingProduct &&
              !productErr &&
              selectedProduct && (
              <div
                className="
                  mt-4
                "
              >
                <CheckoutFormBlock
                  data={{
                    ...buildPurchaseContext({
                      product:
                        selectedProduct,

                      activeRecurring:
                        selectedProduct
                          ?.recurrings
                          ?.find(
                            (
                              recurring:
                                any
                            ) =>
                              recurring
                                .recurring_id ===
                              selectedProduct
                                ?.selectedRecurringId
                          ) ||
                        selectedProduct
                          ?.recurrings?.[0] ||
                        null,

                      selectedRecurringId:
                        selectedProduct
                          ?.selectedRecurringId ||
                        null,

                      price:
                        selectedProduct
                          ?.price,

                      priceUSD:
                        selectedProduct
                          ?.priceUSD,

                      paymentMethods:
                        selectedProduct
                          ?.paymentMethods,

                      platform: {
                        enabled:
                          true,

                        kind:
                          'credits',

                        pack_key:
                          String(
                            selectedProduct
                              ?.pack_key ||
                            selectedPackKey ||
                            ''
                          ),
                      },
                    }),
                    sellerId: Number(sellerId ?? 0),
                  }}

                  isEditing={
                    false
                  }

                  onSuccess={() => {
                    onOpenChange(
                      false
                    )

                    onCreditsAdded?.()

                    void refreshEnergy()

                    window.dispatchEvent(
                      new CustomEvent(
                        'hubee:credits_refresh'
                      )
                    )

                    window.dispatchEvent(
                      new CustomEvent(
                        'hubee:energy_refresh'
                      )
                    )
                  }}
                />
              </div>
            )}

            {loadingProduct && (
              <div
                className="
                  mt-4
                  flex
                  items-center
                  justify-center
                  gap-2
                  text-[10px]
                  text-muted-foreground/70
                "
              >
                <Loader2
                  className="
                    h-3.5
                    w-3.5
                    animate-spin
                    text-primary
                  "
                />

                Preparando recarga...
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}